import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  clearSessionStorage,
  getSessionStorageValue,
  removeSessionStorageValue,
  setSessionStorageValue,
  VOUCHER_SESSION_KEYS,
} from "../../Utils/SessionStorage.js";

export const VoucherContext = createContext();

export { VOUCHER_SESSION_KEYS } from "../../Utils/SessionStorage.js";

const EMPTY_CONTACT_DETAILS = {
  name: "",
  mobile: "",
  email: "",
};

const normalizeContactDetails = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...EMPTY_CONTACT_DETAILS };
  }
  return {
    name: typeof value.name === "string" ? value.name : "",
    mobile: typeof value.mobile === "string" ? value.mobile : "",
    email: typeof value.email === "string" ? value.email : "",
  };
};

const normalizeGiftMessage = (value) =>
  typeof value === "string" ? value : "";

export const getVoucherUnitPrice = (pkg) =>
  parseFloat(
    pkg?.price_rate?.member_after_6_or_weekends ??
      pkg?.price_rate?.nonMemberPrice ??
      pkg?.price ??
      0
  ) || 0;

/** Drop API `services` keys and normalize items onto `vouchers`. */
export const normalizeVoucherCategories = (list) => {
  if (!Array.isArray(list)) return [];

  return list.map((category) => {
    const { services: legacyItems, ...categoryRest } = category || {};
    const vouchers = Array.isArray(category?.vouchers) ? category.vouchers : [];
    const legacy = Array.isArray(legacyItems) ? legacyItems : [];
    // Prefer non-empty vouchers; fall back to API `services` when vouchers is empty.
    const items = vouchers.length > 0 ? vouchers : legacy;

    return {
      ...categoryRest,
      vouchers: items.map((voucher) => {
        const { is_service_available: legacyAvailable, ...voucherRest } =
          voucher || {};
        return {
          ...voucherRest,
          is_available:
            voucher?.is_available !== false && legacyAvailable !== false,
        };
      }),
    };
  });
};

export const VoucherProvider = ({ children }) => {
  const [voucherList, setVoucherList] = useState(
    () =>
      normalizeVoucherCategories(
        getSessionStorageValue(VOUCHER_SESSION_KEYS.voucherList) || []
      )
  );
  const [selectedVouchers, setSelectedVouchers] = useState(
    () => getSessionStorageValue(VOUCHER_SESSION_KEYS.selectedVouchers) || []
  );
  const [selectedVoucherIDs, setSelectedVoucherIDs] = useState(
    () => getSessionStorageValue(VOUCHER_SESSION_KEYS.selectedVoucherIds) || []
  );
  const [voucherCount, setVoucherCount] = useState(
    () => getSessionStorageValue(VOUCHER_SESSION_KEYS.voucherCount) || 0
  );
  const [voucherTotal, setVoucherTotal] = useState(
    () => getSessionStorageValue(VOUCHER_SESSION_KEYS.voucherTotal) || 0
  );
  const [isVoucherCartActive, setIsVoucherCartActive] = useState(() => {
    const stored =
      getSessionStorageValue(VOUCHER_SESSION_KEYS.selectedVouchers) || [];
    return Array.isArray(stored) && stored.length > 0;
  });

  useEffect(() => {
    if (!Array.isArray(selectedVouchers)) return;
    const ids = selectedVouchers.map((voucher) => voucher.id);
    setSelectedVoucherIDs(ids);
    if (selectedVouchers.length === 0) {
      ['selectedVouchers', 'selectedVoucherIds', 'voucherCount', 'voucherTotal']
        .forEach((key) => removeSessionStorageValue(VOUCHER_SESSION_KEYS[key]));
      return;
    }
    setSessionStorageValue(VOUCHER_SESSION_KEYS.selectedVouchers, selectedVouchers);
    setSessionStorageValue(VOUCHER_SESSION_KEYS.selectedVoucherIds, ids);
    setSessionStorageValue(VOUCHER_SESSION_KEYS.voucherCount, voucherCount);
    setSessionStorageValue(VOUCHER_SESSION_KEYS.voucherTotal, voucherTotal);
  }, [selectedVouchers, voucherCount, voucherTotal]);

  useEffect(() => {
    if (!Array.isArray(voucherList) || voucherList.length === 0) return;
    setSessionStorageValue(VOUCHER_SESSION_KEYS.voucherList, voucherList);
  }, [voucherList]);

  const addVoucher = useCallback((selectedPkg) => {
    const price = getVoucherUnitPrice(selectedPkg);
    setSelectedVouchers((prev) => [...prev, selectedPkg]);
    setVoucherTotal((prevTotal) => prevTotal + price);
    setVoucherCount((prevCount) => prevCount + 1);
    setIsVoucherCartActive(true);
  }, []);

  const removeVoucher = useCallback((selectedPkg) => {
    const adj = { ...selectedPkg, count: selectedPkg.count + 1 };
    const price = getVoucherUnitPrice(adj);
    setSelectedVouchers((prevVouchers) => {
      const updatedVouchers = prevVouchers.filter(
        (voucher) => !(voucher.id === adj.id && voucher.count === adj.count)
      );
      setIsVoucherCartActive(updatedVouchers.length > 0);
      return updatedVouchers;
    });
    setVoucherTotal((prevTotal) => Math.max(0, prevTotal - price));
    setVoucherCount((prevCount) => Math.max(0, prevCount - 1));
  }, []);

  const updateVoucherQuantity = useCallback((pkg, nextQuantity) => {
    if (!pkg?.id) return;

    const target = Math.max(0, Number(nextQuantity) || 0);

    setSelectedVouchers((prev) => {
      const others = prev.filter(
        (voucher) => String(voucher.id) !== String(pkg.id)
      );
      const currentForId = prev.filter(
        (voucher) => String(voucher.id) === String(pkg.id)
      );
      const currentCount = currentForId.length;
      const unitPrice = getVoucherUnitPrice(pkg);

      let nextForId = currentForId;
      if (target > currentCount) {
        const toAdd = [];
        for (let i = currentCount; i < target; i += 1) {
          toAdd.push({
            ...pkg,
            count: i + 1,
            isCounterVisible: true,
            price: unitPrice,
            category_id: pkg.category_id,
            category_name: pkg.category_name,
          });
        }
        nextForId = [...currentForId, ...toAdd];
      } else if (target < currentCount) {
        nextForId = currentForId
          .slice()
          .sort((a, b) => (a.count || 0) - (b.count || 0))
          .slice(0, target);
      }

      const nextSelected = [...others, ...nextForId];
      const nextTotal = nextSelected.reduce(
        (sum, voucher) => sum + getVoucherUnitPrice(voucher),
        0
      );

      setVoucherCount(nextSelected.length);
      setVoucherTotal(nextTotal);
      setIsVoucherCartActive(nextSelected.length > 0);
      return nextSelected;
    });

    setVoucherList((prevList) =>
      (prevList || []).map((category) => ({
        ...category,
        vouchers: Array.isArray(category.vouchers)
          ? category.vouchers.map((item) =>
              String(item.id) === String(pkg.id)
                ? {
                    ...item,
                    count: target,
                    isCounterVisible: target > 0,
                  }
                : item
            )
          : [],
      }))
    );
  }, []);

  const [billingDetails, setBillingDetails] = useState(() =>
    normalizeContactDetails(
      getSessionStorageValue(VOUCHER_SESSION_KEYS.billingDetails)
    )
  );

  const [recipientDetails, setRecipientDetails] = useState(() =>
    normalizeContactDetails(
      getSessionStorageValue(VOUCHER_SESSION_KEYS.recipientDetails)
    )
  );

  const [giftMessage, setGiftMessage] = useState(() =>
    normalizeGiftMessage(
      getSessionStorageValue(VOUCHER_SESSION_KEYS.giftMessage, "")
    )
  );

  const [orderData, setOrderData] = useState(() =>
    getSessionStorageValue(VOUCHER_SESSION_KEYS.orderData) || null
  );

  const [orderMessage, setOrderMessage] = useState(() =>
    getSessionStorageValue(VOUCHER_SESSION_KEYS.orderMessage) || ""
  );

  useEffect(() => {
    if (!Object.values(normalizeContactDetails(billingDetails)).some(Boolean)) {
      removeSessionStorageValue(VOUCHER_SESSION_KEYS.billingDetails);
      return;
    }
    setSessionStorageValue(
      VOUCHER_SESSION_KEYS.billingDetails,
      normalizeContactDetails(billingDetails)
    );
  }, [billingDetails]);

  useEffect(() => {
    if (!Object.values(normalizeContactDetails(recipientDetails)).some(Boolean)) {
      removeSessionStorageValue(VOUCHER_SESSION_KEYS.recipientDetails);
      return;
    }
    setSessionStorageValue(
      VOUCHER_SESSION_KEYS.recipientDetails,
      normalizeContactDetails(recipientDetails)
    );
  }, [recipientDetails]);

  useEffect(() => {
    if (!giftMessage) {
      removeSessionStorageValue(VOUCHER_SESSION_KEYS.giftMessage);
      return;
    }
    setSessionStorageValue(
      VOUCHER_SESSION_KEYS.giftMessage,
      normalizeGiftMessage(giftMessage)
    );
  }, [giftMessage]);

  useEffect(() => {
    if (orderData) {
      setSessionStorageValue(VOUCHER_SESSION_KEYS.orderData, orderData);
    } else {
      removeSessionStorageValue(VOUCHER_SESSION_KEYS.orderData);
    }
  }, [orderData]);

  useEffect(() => {
    if (orderMessage) {
      setSessionStorageValue(VOUCHER_SESSION_KEYS.orderMessage, orderMessage);
    } else {
      removeSessionStorageValue(VOUCHER_SESSION_KEYS.orderMessage);
    }
  }, [orderMessage]);

  const clearVoucherCart = useCallback(() => {
    setSelectedVouchers([]);
    setSelectedVoucherIDs([]);
    setVoucherCount(0);
    setVoucherTotal(0);
    setIsVoucherCartActive(false);
    setBillingDetails({ ...EMPTY_CONTACT_DETAILS });
    setRecipientDetails({ ...EMPTY_CONTACT_DETAILS });
    setGiftMessage("");
    setOrderData(null);
    setOrderMessage('');
    setVoucherList((prevList) =>
      (prevList || []).map((category) => ({
        ...category,
        vouchers: Array.isArray(category.vouchers)
          ? category.vouchers.map((item) => ({
              ...item,
              count: 0,
              isCounterVisible: false,
            }))
          : [],
      }))
    );

   clearSessionStorage()
  }, []);

  return (
    <VoucherContext.Provider
      value={{
        voucherList,
        setVoucherList,
        selectedVouchers,
        setSelectedVouchers,
        selectedVoucherIDs,
        setSelectedVoucherIDs,
        voucherCount,
        setVoucherCount,
        voucherTotal,
        setVoucherTotal,
        isVoucherCartActive,
        setIsVoucherCartActive,
        addVoucher,
        removeVoucher,
        updateVoucherQuantity,
        clearVoucherCart,
        billingDetails,
        setBillingDetails,
        recipientDetails,
        setRecipientDetails,
        giftMessage,
        setGiftMessage,
        orderData,
        setOrderData,
        orderMessage,
        setOrderMessage,
      }}
    >
      {children}
    </VoucherContext.Provider>
  );
};

export const useVoucherContext = () => useContext(VoucherContext);
